import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import oneSignalService from '@/common/services/oneSignalService';
import {
  unlockAudio,
  playNotificationSound,
} from '@/common/utils/notificationAudio';
import { buildNotificationNavigation } from '@/common/utils/notificationRouter';
import { OneSignalContext } from './oneSignalContext';
import notificationIcon from '@/assets/notification.png';
import {
  clearNotificationPromptDismissal,
  dismissNotificationPrompt,
  isNotificationPromptDismissed,
} from '@/common/utils/notificationPromptPreference';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getExternalUserId(user) {
  if (!user) return null;
  const rawId = user.id ?? user.user_id ?? user.pk ?? null;
  if (rawId == null) return null;
  const strId = String(rawId).trim();
  if (!strId || strId === 'undefined' || strId === 'null' || strId === '0')
    return null;
  return strId.startsWith('user_') ? strId : `user_${strId}`;
}

function refreshSubscriptionState(
  setPermissionStatus,
  setSubscriptionId,
  setIsOptedIn,
) {
  const snapshot = oneSignalService.getSubscriptionSnapshot();
  setPermissionStatus(snapshot.permissionStatus);
  setSubscriptionId(snapshot.subscriptionId);
  if (setIsOptedIn) setIsOptedIn(oneSignalService.isOptedIn());
  return snapshot;
}

// ─── Foreground toast ─────────────────────────────────────────────────────────

// Tracks recently shown notification IDs to deduplicate rapid SDK re-fires of the SAME push.
// Key = notifId, Value = timestamp shown. Entries expire after 3s.
const _recentlyShownIds = new Map();

function showForegroundToast(notification, onNavigate) {
  const title = notification.title || notification.heading || 'Notification';
  const message = notification.message || notification.body || '';

  // Build a stable unique ID for this notification.
  // Prefer OneSignal's notificationId (most reliable), then fall back to title+message hash.
  const notifId =
    notification.id ||
    notification.notificationId ||
    notification.notification_id ||
    `${title}::${message}`;

  const now = Date.now();

  // Deduplicate: if the SAME notification was shown within last 3 seconds, skip.
  const lastShown = _recentlyShownIds.get(String(notifId));
  if (lastShown && now - lastShown < 3000) {
    console.info(
      '[OneSignal] Deduplicated duplicate foreground push:',
      notifId,
    );
    return;
  }
  _recentlyShownIds.set(String(notifId), now);

  // Prune old entries so map doesn't grow forever.
  _recentlyShownIds.forEach((ts, key) => {
    if (now - ts > 10000) _recentlyShownIds.delete(key);
  });

  // Play notification sound
  playNotificationSound();

  // Each unique notification gets its own stable toast ID.
  // This way react-hot-toast shows ALL different notifications at the same time (like Slack).
  const safeId = String(notifId)
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .substring(0, 64);
  const toastId = `onesignal-fg-${safeId}`;

  // Display in-app toast — separate card per notification, stacked like Slack.
  toast(
    (t) => (
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          toast.dismiss(t.id);
          if (onNavigate) onNavigate(notification);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toast.dismiss(t.id);
            if (onNavigate) onNavigate(notification);
          }
        }}
        className="relative flex items-start gap-3.5 w-full cursor-pointer select-none group"
      >
        {/* Left Icon Badge */}
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white shrink-0 shadow-sm mt-0.5">
          <img
            src={notificationIcon}
            alt="Notification"
            className="w-5 h-5 object-contain"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-2">
          <p className="text-sm font-semibold text-gray-900 leading-snug tracking-tight">
            {title}
          </p>
          {message ? (
            <p className="mt-1 text-xs text-gray-600 leading-relaxed break-words line-clamp-2">
              {message}
            </p>
          ) : null}
        </div>

        {/* Close Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toast.dismiss(t.id);
          }}
          className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1 rounded-full transition-colors shrink-0 -mr-1 -mt-1"
          aria-label="Dismiss notification"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    ),
    {
      id: toastId,
      duration: 6000,
      position: 'top-right',
      style: {
        background: '#ffffff',
        color: '#111827',
        border: '1px solid #E5E7EB',
        borderRadius: '16px',
        boxShadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
        padding: '14px 16px',
        maxWidth: '380px',
        zIndex: 999999,
      },
    },
  );
}

// ─── Provider ─────────────────────────────────────────────────────────────────

/**
 * OneSignalProvider — initializes OneSignal SDK, manages user identity,
 * handles foreground notifications and push permission UI state.
 *
 * OneSignal owns device ↔ user mapping via external_id = user.id.
 * No backend push-subscription POST needed.
 */
export function OneSignalProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(() => oneSignalService.isInitialized());
  const [permissionStatus, setPermissionStatus] = useState(
    oneSignalService.getPermissionStatus(),
  );
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [isOptedIn, setIsOptedIn] = useState(() =>
    oneSignalService.isOptedIn(),
  );
  const [bannerDismissed, setBannerDismissed] = useState(() =>
    isNotificationPromptDismissed(),
  );
  const lastExternalIdRef = useRef(null);
  const subscriptionChangeTimerRef = useRef(null);

  const navigateFromNotification = useCallback(
    (notification) => {
      try {
        const { path, state } = buildNotificationNavigation(notification);
        navigate(path, { state });
      } catch {
        navigate('/dashboard');
      }
    },
    [navigate],
  );

  const identifyFromSession = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const user = readStoredUser();
    const externalId = getExternalUserId(user);

    refreshSubscriptionState(
      setPermissionStatus,
      setSubscriptionId,
      setIsOptedIn,
    );

    if (!token || !externalId) {
      if (lastExternalIdRef.current !== null) {
        await oneSignalService.logout();
        lastExternalIdRef.current = null;
      }
      return;
    }

    await oneSignalService.login(externalId, {
      company_id: user?.company_id || localStorage.getItem('company_id') || '',
      role: user?.role || '',
      email: user?.email || '',
    });

    refreshSubscriptionState(
      setPermissionStatus,
      setSubscriptionId,
      setIsOptedIn,
    );

    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      lastExternalIdRef.current = String(externalId);
    }
  }, []);

  // Unlock browser audio autoplay policy on first user interaction.
  useEffect(() => {
    const handleInteraction = () => unlockAudio();
    window.addEventListener('click', handleInteraction, {
      capture: true,
      once: true,
    });
    window.addEventListener('keydown', handleInteraction, {
      capture: true,
      once: true,
    });
    window.addEventListener('touchstart', handleInteraction, {
      capture: true,
      once: true,
    });
    return () => {
      window.removeEventListener('click', handleInteraction, { capture: true });
      window.removeEventListener('keydown', handleInteraction, {
        capture: true,
      });
      window.removeEventListener('touchstart', handleInteraction, {
        capture: true,
      });
    };
  }, []);

  // ── Init effect (runs once on mount) ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const cleanups = [];

    if (oneSignalService.isInitialized()) {
      setReady(true);
      refreshSubscriptionState(
        setPermissionStatus,
        setSubscriptionId,
        setIsOptedIn,
      );
    }

    (async () => {
      const ok = await oneSignalService.init();
      if (cancelled) return;
      if (ok || oneSignalService.isInitialized()) {
        setReady(true);
      } else {
        setReady(false);
        return;
      }

      refreshSubscriptionState(
        setPermissionStatus,
        setSubscriptionId,
        setIsOptedIn,
      );
      await identifyFromSession();

      // Handle pending route from a background notification click.
      const pending = oneSignalService.consumePendingRoute();
      if (pending?.path) {
        navigate(pending.path, { state: pending.state || undefined });
      }

      // Permission granted → re-link user.
      cleanups.push(
        oneSignalService.onPermissionChange(async (granted) => {
          setPermissionStatus(
            granted ? 'granted' : oneSignalService.getPermissionStatus(),
          );
          if (granted) {
            await oneSignalService.waitForPushToken({ timeoutMs: 5000 });
            await identifyFromSession();
          }
        }),
      );

      // Subscription changed → refresh UI state and re-identify user.
      // NOTE: do NOT call resetLoginState() here — login() itself fires a
      // subscriptionChange event, and resetting would create a re-login loop
      // that causes 400 "existingOnesignalId" conflicts.
      // The loginUser() guard (loggedInExternalId) already skips re-login
      // when the user is already linked.
      cleanups.push(
        oneSignalService.onSubscriptionChange(() => {
          refreshSubscriptionState(
            setPermissionStatus,
            setSubscriptionId,
            setIsOptedIn,
          );
          // If notifications are disabled/opted-out, do not trigger auto-login
          if (!oneSignalService.isOptedIn()) {
            return;
          }
          // Debounce: SDK fires multiple change events during optIn(). Only act once settled.
          if (subscriptionChangeTimerRef.current) {
            clearTimeout(subscriptionChangeTimerRef.current);
          }
          subscriptionChangeTimerRef.current = setTimeout(() => {
            identifyFromSession();
          }, 800);
        }),
      );

      // Foreground notification → prevent external native OS notification, show only in-app toast + sound.
      cleanups.push(
        oneSignalService.onForegroundWillDisplay((event) => {
          console.info('[OneSignal] Foreground push received:', event);

          // Suppress external OS/Chrome native push notification when web app is open
          if (typeof event?.preventDefault === 'function') {
            event.preventDefault();
          }

          // If notifications are disabled/opted-out, do not display in-app toast
          if (!oneSignalService.isOptedIn()) {
            console.info(
              '[OneSignal] Foreground push suppressed: user opted out.',
            );
            return;
          }

          const notification = oneSignalService.normalizePushPayload(event);
          showForegroundToast(notification, navigateFromNotification);
        }),
      );

      // Notification click → navigate to the relevant route.
      cleanups.push(
        oneSignalService.onNotificationClick((event) => {
          const notification = oneSignalService.normalizePushPayload(event);
          try {
            const { path, state } = buildNotificationNavigation(notification);
            if (document.visibilityState === 'visible') {
              navigate(path, { state });
            } else {
              oneSignalService.storePendingRoute(path, state);
              navigate(path, { state });
            }
          } catch {
            navigate('/dashboard');
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
      if (subscriptionChangeTimerRef.current) {
        clearTimeout(subscriptionChangeTimerRef.current);
      }
      cleanups.forEach((dispose) => {
        try {
          dispose();
        } catch {
          /* ignore */
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-identify user on route change (handles login/logout flow).
  useEffect(() => {
    if (!ready) return;
    identifyFromSession();
  }, [ready, location.pathname, identifyFromSession]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const disableNotifications = useCallback(async () => {
    const ok = await oneSignalService.disableNotifications();
    refreshSubscriptionState(
      setPermissionStatus,
      setSubscriptionId,
      setIsOptedIn,
    );
    return ok;
  }, []);

  const enableNotifications = useCallback(async () => {
    let status = oneSignalService.getPermissionStatus();
    if (status !== 'granted') {
      status = await oneSignalService.requestPermission();
    } else {
      await oneSignalService.enableNotifications();
    }
    refreshSubscriptionState(
      setPermissionStatus,
      setSubscriptionId,
      setIsOptedIn,
    );
    if (status === 'granted') {
      await oneSignalService.waitForPushToken({ timeoutMs: 15000 });
      refreshSubscriptionState(
        setPermissionStatus,
        setSubscriptionId,
        setIsOptedIn,
      );
      await identifyFromSession();
    }
    return status;
  }, [identifyFromSession]);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    dismissNotificationPrompt();
  }, []);

  const logout = useCallback(async () => {
    await oneSignalService.logout();
    lastExternalIdRef.current = null;
    setSubscriptionId(null);
    setIsOptedIn(false);
    setPermissionStatus(oneSignalService.getPermissionStatus());
    // Next login only. No session id survives token rotation; logout is the boundary.
    setBannerDismissed(false);
    clearNotificationPromptDismissal();
  }, []);

  // ── Context value ────────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      ready,
      configured: oneSignalService.isConfigured(),
      supported: oneSignalService.isPushSupported(),
      permissionStatus,
      subscriptionId,
      isOptedIn,
      bannerDismissed,
      enableNotifications,
      disableNotifications,
      dismissBanner,
      logout,
      identifyFromSession,
      playSound: playNotificationSound,
      consumePendingRoute: () => {
        const pending = oneSignalService.consumePendingRoute();
        if (pending?.path)
          navigate(pending.path, { state: pending.state || undefined });
        return pending;
      },
    }),
    [
      ready,
      permissionStatus,
      subscriptionId,
      isOptedIn,
      bannerDismissed,
      enableNotifications,
      disableNotifications,
      dismissBanner,
      logout,
      identifyFromSession,
      navigate,
    ],
  );

  return (
    <OneSignalContext.Provider value={value}>
      {children}
    </OneSignalContext.Provider>
  );
}
